import { FC, useEffect } from "react";
import Head from "next/head";
import Image from "next/image";
import { timeSinceFormatter } from "@utils/formatters/timeSinceFormatter";
import { Apply } from "./fields/Apply";
import { SummaryBlock } from "./blocks/SummaryBlock";
import { AnalyticsEvent } from "@utils/redis/analytics/AnalyticsEvent";
import { RemoteRegion } from "data/remoteRegions/remoteRegions";
import { JobPostingJsonLd } from "@components/JsonLd/JobPostingJsonLd";
import { gtagEvent } from "@services/googleAnalytics";
import {
  JobPost,
  Location,
  Remote,
  Extras,
  Organisation,
  Compensation,
} from "@prisma/client";
import { AssignedTag, NewTag } from "types/tags";
import Link from "next/link";
import { mixpanelEvent } from "@services/mixpanel";
import { JobPostPreview } from "./JobPostPreview";
import { getCompensationAsString } from "@utils/db/Compensation/getCompensationAsString";
import { BanknotesIcon } from "@heroicons/react/24/outline";
import { classNames } from "@utils/classNames";

interface Props {
  jobPost: JobPost & {
    compensation: Compensation;
    hiringOrganisation: Organisation;
    sourcingOrganisation: Organisation;
    locations: Location[];
    remote: Remote & { regionsPopulated: RemoteRegion[] };
    extras: Extras;
    tags?: (AssignedTag & NewTag)[];
  };
  similarJobPosts?: (JobPost & {
    compensation: Compensation;
    hiringOrganisation: Organisation;
    locations: Location[];
    remote: Remote;
    extras: Extras;
  })[];
  avoidAppleReaderMode?: boolean;
  canonicalUrl?: string;
}

export const JobPostDetail: FC<Props> = ({
  jobPost,
  similarJobPosts,
  avoidAppleReaderMode,
  canonicalUrl,
}) => {
  const appleReaderModeMenu = `<h3 class="mt-6 mb-6 text-xs font-black text-blue-600 sm:hidden"><a href="/">➔ All jobs</a></h3>
  ${
    jobPost.hiringOrganisation?.name && jobPost.hiringOrganisation?.slug
      ? `<h3 class="mb-6 text-xs font-black text-blue-600 sm:hidden"><a href="/company/${jobPost.hiringOrganisation?.slug}">➔ Jobs at ${jobPost.hiringOrganisation?.name}</a></h3>`
      : ""
  }
  <h3 class="mb-10 text-xs font-black text-blue-600 sm:hidden"><a href="${
    jobPost.applyUrl
  }" target="_blank">➔ Apply for this job</a></h3>`;

  useEffect(() => {
    // Google analytics: Record job post detail view.
    gtagEvent({
      name: "view_item",
      payload: {
        items: [{ id: jobPost.id }],
      },
    });

    // Mixpanel: Record job post detail view.
    mixpanelEvent({
      name: AnalyticsEvent.JOB_POST_DETAIL,
      payload: {
        id: jobPost.id,
      },
    });

    // Internal analytics: Record job post detail view.
    fetch("/api/analytics/collect", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        event: AnalyticsEvent.JOB_POST_DETAIL,
        ids: [jobPost.id],
      }),
    });
  }, []);

  return (
    <>
      <Head>
        <meta
          name="description"
          content={`${jobPost.description.slice(0, 130)}...`}
        />
        {/* -------------------------------------------------------------------------------------- */}
        {/* Twitter card metadata  */}
        {/* -------------------------------------------------------------------------------------- */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={`${jobPost.title}${
            jobPost.hiringOrganisation?.name
              ? ` at ${jobPost.hiringOrganisation?.name}`
              : ""
          }`}
        />
        <meta
          name="twitter:description"
          content={`${jobPost.description.slice(0, 130)}...`}
        />
        <meta name="twitter:site" content="@jobsinjs" />
        <meta
          name="twitter:image"
          content={`${process.env["NEXT_PUBLIC_BASE_URL"]}/api/job-posts/${jobPost.id}/og-image`}
        />
        {/* -------------------------------------------------------------------------------------- */}
        {/* OG metadata  */}
        {/* -------------------------------------------------------------------------------------- */}
        {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
        <meta
          name="og:title"
          content={`${jobPost.title}${
            jobPost.hiringOrganisation?.name
              ? ` at ${jobPost.hiringOrganisation?.name}`
              : ""
          }`}
        />
        <meta
          name="og:description"
          content={`${jobPost.description.slice(0, 130)}...`}
        />
        <meta
          name="og:image"
          content={`${process.env["NEXT_PUBLIC_BASE_URL"]}/api/job-posts/${jobPost.id}/og-image`}
        />
        {/* -------------------------------------------------------------------------------------- */}
      </Head>

      {/* JSON+LD */}
      <JobPostingJsonLd jobPost={jobPost} />

      <div>
        <div className="flex justify-between py-4">
          <div>
            {/* ------------------------- */}

            <div className="flex flex-row">
              <Link
                href={`/company/${jobPost.hiringOrganisation?.slug}`}
                className="shrink-0 w-16"
              >
                {jobPost.hiringOrganisation?.favicon ? (
                  <Image
                    className="rounded"
                    width={48}
                    height={48}
                    src={`${process.env.NEXT_PUBLIC_SUPABASE_PROJECT_URL}/storage/v1/object/public/public${jobPost?.hiringOrganisation?.favicon}`}
                    alt={`${jobPost?.hiringOrganisation?.name || ""} logo`}
                  />
                ) : (
                  <div className="flex justify-center items-center w-10 h-10 rounded bg-gray-100 ">
                    {jobPost.hiringOrganisation?.name?.[0]}
                  </div>
                )}
              </Link>
              <div className="flex flex-col">
                <h1 className="shrink-1 text-2xl font-bold">{jobPost.title}</h1>
                {jobPost?.hiringOrganisation?.name &&
                  jobPost.hiringOrganisation?.slug && (
                    <p>
                      <Link
                        href={`/company/${jobPost.hiringOrganisation?.slug}`}
                      >
                        {jobPost.hiringOrganisation.name}
                      </Link>
                    </p>
                  )}

                {getCompensationAsString(jobPost.compensation) && (
                  <div className="my-2 text-md font-boldX">
                    <BanknotesIcon className="inline w-4 h-4 mr-1" />
                    {getCompensationAsString(jobPost.compensation)}
                  </div>
                )}
                <div className="mt-2 text-xxs text-gray-400 uppercase">
                  {timeSinceFormatter(new Date(jobPost.createdAt), {
                    addSuffix: true,
                  })}
                </div>
              </div>
            </div>
            {/* ------------------------- */}
          </div>
          <div className="hidden pl-4 sm:block">
            <Apply
              id={jobPost.id}
              applyUrl={jobPost.applyUrl}
              cN="inline-block mb-4 px-4 sm:px-10"
            />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-12 gap-8">
        <div
          className={classNames(
            "container sm:col-span-8",
            avoidAppleReaderMode ? "col-span-9" : "col-span-12"
          )}
        >
          <div className="py-4 border-t">
            <div
              className="job-post-description text-base"
              dangerouslySetInnerHTML={{
                __html: `${appleReaderModeMenu} ${jobPost.description}` || "",
              }}
            />
          </div>
          <div className="my-6">
            <Apply
              id={jobPost.id}
              applyUrl={jobPost.applyUrl}
              cN="inline-block mb-4 px-4"
            />
          </div>
        </div>
        <div className="col-span-12 sm:col-span-4">
          <div className="p-4 pb-6 border rounded-md">
            <SummaryBlock jobPost={jobPost} />
          </div>
        </div>
      </div>
      <div>
        {similarJobPosts?.length > 0 && (
          <div className="max-w-4xl mt-20">
            <h2 className="mb-4 text-xl font-bold">
              More jobs currently hiring JavaScript developers
            </h2>

            <ul>
              {similarJobPosts?.map((similarJobPost) => (
                <JobPostPreview
                  key={similarJobPost.id}
                  jobPost={similarJobPost}
                  cN="pt-3 pb-3 text-xs border-t"
                />
              ))}
            </ul>
          </div>
        )}
      </div>
    </>
  );
};
